// theme.js — Chakra side of the design tokens.
// The CSS side lives in components/styles/theme.css; keep the two in step.

import { extendTheme } from '@chakra-ui/react';

const config = {
  initialColorMode: 'dark',
  useSystemColorMode: false,
};

// Amber accent used across the whole site
const brand = {
  50: '#fff8ec',
  100: '#ffeed2',
  200: '#ffdda6',
  300: '#ffcb77',
  400: '#ffbb56',
  500: '#f5a832',
  600: '#d98a1c',
  700: '#a86612',
  800: '#74450d',
  900: '#422607',
};

const surface = {
  base: '#0d1117',
  panel: '#12161f',
  raised: '#161b24',
};

const fonts = {
  heading: "'Kanit', sans-serif",
  body: "'Kanit', sans-serif",
  mono: "ui-monospace, 'SFMono-Regular', Menlo, Consolas, monospace",
};

const theme = extendTheme({
  config,
  colors: { brand, surface },
  fonts,
  semanticTokens: {
    colors: {
      ink: { default: '#1a202c', _dark: '#f4f1ea' },
      'ink-soft': {
        default: 'rgba(26, 32, 44, 0.78)',
        _dark: 'rgba(244, 241, 234, 0.78)',
      },
      'ink-dim': {
        default: 'rgba(26, 32, 44, 0.55)',
        _dark: 'rgba(244, 241, 234, 0.55)',
      },
      hairline: {
        default: 'rgba(0, 0, 0, 0.1)',
        _dark: 'rgba(255, 255, 255, 0.08)',
      },
    },
  },
  styles: {
    global: {
      body: {
        bg: 'surface.base',
        color: 'ink',
        fontWeight: 300,
      },
      '::selection': {
        bg: 'rgba(255, 187, 86, 0.25)',
      },
      // Chakra's default focus ring is blue; keep it on the accent instead
      '*:focus-visible': {
        boxShadow: '0 0 0 2px rgba(255, 187, 86, 0.6) !important',
        outline: 'none',
      },
    },
  },
  components: {
    Heading: {
      baseStyle: {
        fontWeight: 500,
        letterSpacing: '-0.01em',
      },
    },
    Modal: {
      baseStyle: {
        dialog: {
          bg: 'surface.panel',
          border: '1px solid',
          borderColor: 'hairline',
          borderRadius: '16px',
          boxShadow: '0 24px 60px rgba(0, 0, 0, 0.55)',
        },
        overlay: {
          bg: 'rgba(6, 8, 12, 0.72)',
          backdropFilter: 'blur(4px)',
        },
        header: {
          fontWeight: 500,
        },
      },
    },
    Divider: {
      baseStyle: {
        borderColor: 'hairline',
        opacity: 1,
      },
    },
    Button: {
      defaultProps: {
        colorScheme: 'brand',
      },
    },
    Badge: {
      defaultProps: {
        colorScheme: 'brand',
      },
    },
    Link: {
      baseStyle: {
        _hover: { textDecoration: 'none', color: 'brand.400' },
      },
    },
  },
});

export default theme;
